import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';

const WEDDING_DATE = new Date('2026-09-05T14:00:00');

const getPhase = () => {
  const now = new Date();
  if (now.toDateString() === WEDDING_DATE.toDateString()) return 'today';
  return now > WEDDING_DATE ? 'after' : 'before';
};

const calculateTimeLeft = () => {
  const now = new Date();
  const difference = WEDDING_DATE - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((difference / 1000 / 60) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
};

const TimeUnit = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <motion.span
      key={value}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="font-display text-4xl md:text-6xl text-gold drop-shadow-sm"
      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}
    >
      {String(value).padStart(2, '0')}
    </motion.span>
    <span className="text-xs uppercase tracking-widest text-gold/80 mt-2">{label}</span>
  </div>
);

const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [phase, setPhase] = useState(getPhase());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
      setPhase(getPhase());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (phase === 'today') {
    return (
      <motion.div
        data-testid="countdown-wedding-day"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="text-center"
      >
        <motion.div
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 1.6, repeat: Infinity }}
          className="inline-block mb-3"
        >
          <Heart className="w-12 h-12 md:w-16 md:h-16 text-gold fill-gold drop-shadow" />
        </motion.div>
        <p className="font-display text-4xl md:text-6xl text-gold drop-shadow-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
          C'est le grand jour !
        </p>
        <p className="text-white/90 text-sm md:text-base uppercase tracking-widest mt-3">
          Mélanie & Cédric se disent oui aujourd'hui
        </p>
      </motion.div>
    );
  }

  if (phase === 'after') {
    return (
      <motion.div
        data-testid="countdown-after-wedding"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        className="text-center"
      >
        <Heart className="w-10 h-10 md:w-12 md:h-12 text-gold fill-gold drop-shadow inline-block mb-3" />
        <p className="font-display text-3xl md:text-5xl text-gold drop-shadow-sm" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.1)' }}>
          Merci pour ce jour magique
        </p>
        <p className="text-white/90 text-sm md:text-base uppercase tracking-widest mt-3">
          Revivez-le en photos dans l'album
        </p>
      </motion.div>
    );
  }

  return (
    <div data-testid="countdown" className="flex items-center justify-center gap-6 md:gap-12">
      <TimeUnit value={timeLeft.days} label="Jours" />
      <span className="font-display text-3xl text-gold drop-shadow-sm">:</span>
      <TimeUnit value={timeLeft.hours} label="Heures" />
      <span className="font-display text-3xl text-gold drop-shadow-sm">:</span>
      <TimeUnit value={timeLeft.minutes} label="Minutes" />
      <span className="font-display text-3xl text-gold drop-shadow-sm hidden md:block">:</span>
      <div className="hidden md:block">
        <TimeUnit value={timeLeft.seconds} label="Secondes" />
      </div>
    </div>
  );
};

export default Countdown;
